// Three-pane editor — the heart of the SPA.
// Left:  raw script (read-only on this page; editing is via Edit script button)
// Centre: directed view (or "Direct it" button when no IR yet)
// Right: provider + voice picker + render controls + audio player

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, projects, TTS_PROVIDERS, type DirectorChoice, type ProjectFull, type TtsProvider } from '../api.js';
import { DirectedView } from '../components/directed-view.js';
import { AudioSettingsPanel } from '../components/audio-settings-panel.js';
import { VoicePicker } from '../components/voice-picker.js';
import { HistoryDrawer } from '../components/history-drawer.js';
import { MultiVoicePanel } from '../components/multi-voice-panel.js';
import { AudioPlayer } from '../components/ui/audio-player.js';
import { Spinner } from '../components/ui/spinner.js';
import { useToast } from '../components/ui/toast.js';

const DIRECTOR_OPTIONS: { id: DirectorChoice; label: string; hint: string }[] = [
  { id: 'deepseek', label: 'DeepSeek', hint: 'cheapest, fast' },
  { id: 'gemini', label: 'Gemini 2.5 Pro', hint: 'strong reasoning' },
  { id: 'openai', label: 'OpenAI', hint: 'gpt-4.1-mini default' },
  { id: 'anthropic', label: 'Claude Sonnet', hint: 'best quality, paid' },
];

const TTS_OPTIONS: { id: TtsProvider; label: string; hint: string }[] = TTS_PROVIDERS
  .filter((p) => p.id !== 'playht') // dead — keep type, drop UI affordance
  .map((p) => ({ id: p.id, label: p.label, hint: p.hint }));

export function ProjectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [project, setProject] = useState<ProjectFull | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingScript, setEditingScript] = useState(false);
  const [scriptDraft, setScriptDraft] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);

  const [directorChoice, setDirectorChoice] = useState<DirectorChoice>('deepseek');
  const [directing, setDirecting] = useState(false);
  const [directError, setDirectError] = useState<string | null>(null);
  const [directProgress, setDirectProgress] = useState<{
    chunksDone: number;
    chunksTotal: number;
    etaMs: number | null;
    message: string;
  } | null>(null);
  const directEventSourceRef = useRef<EventSource | null>(null);

  const [ttsProvider, setTtsProvider] = useState<TtsProvider>('openai');
  const [voiceId, setVoiceId] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [renderId, setRenderId] = useState<string | null>(null);
  const [renderProgress, setRenderProgress] = useState<{
    sectionsDone: number;
    sectionsTotal: number;
    currentSectionId: string | null;
    etaMs: number | null;
    message: string;
  } | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const [busySectionId, setBusySectionId] = useState<string | null>(null);
  const [regenError, setRegenError] = useState<string | null>(null);
  const [audioStale, setAudioStale] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [showMultiVoice, setShowMultiVoice] = useState(false);
  useEffect(() => {
    return () => {
      eventSourceRef.current?.close();
    };
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setError(null);
    projects.get(id)
      .then((r) => {
        if (cancelled) return;
        setProject(r.project);
        setScriptDraft(r.project.script_text);
        setTitleDraft(r.project.title);
        if (r.project.last_render_id) setRenderId(r.project.last_render_id);
        if (r.project.last_render_provider) setTtsProvider(r.project.last_render_provider as TtsProvider);
        if (r.project.last_render_voice_id) setVoiceId(r.project.last_render_voice_id);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load project.'));
    return () => { cancelled = true; };
  }, [id]);

  async function handleDirect() {
    if (!project) return;
    setDirecting(true);
    setDirectError(null);
    setDirectProgress(null);
    try {
      // 1. Enqueue an async direct job (handles short + long scripts; chunks if needed)
      const enqueue = await projects.directAsync(project.id, directorChoice);

      // 2. Subscribe to SSE progress
      const ev = new EventSource(enqueue.events_url, { withCredentials: true } as EventSourceInit);
      directEventSourceRef.current = ev;

      const finishOk = async () => {
        // Refetch the project to pick up the persisted IR
        try {
          const r = await projects.get(project.id);
          setProject(r.project);
        } catch {}
        ev.close();
        directEventSourceRef.current = null;
        setDirecting(false);
        setDirectProgress(null);
        toast.success('Script directed', `Directed with ${directorChoice}.`);
      };

      ev.addEventListener('snapshot', (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data);
          if (data.progress) setDirectProgress(data.progress);
        } catch {}
      });
      ev.addEventListener('progress', (e) => {
        try {
          const p = JSON.parse((e as MessageEvent).data);
          setDirectProgress(p);
        } catch {}
      });
      ev.addEventListener('completed', () => {
        void finishOk();
      });
      ev.addEventListener('failed', (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data);
          const msg = String(data?.error ?? '');
          if (msg.includes('truncated') || msg.includes('MAX_TOKENS')) {
            setDirectError('Script is too long for ' + directorChoice + '. Try Gemini 2.5 Pro or Claude.');
            toast.error('Script too long', 'Try Gemini 2.5 Pro or Claude.');
          } else {
            setDirectError(msg || 'Directing failed.');
            toast.error('Directing failed', msg || undefined);
          }
        } catch {
          setDirectError('Directing failed.');
          toast.error('Directing failed');
        }
        ev.close();
        directEventSourceRef.current = null;
        setDirecting(false);
        setDirectProgress(null);
      });
      ev.onerror = () => {};
    } catch (err) {
      setDirectError(err instanceof ApiError ? err.message : 'Directing failed.');
      setDirecting(false);
      setDirectProgress(null);
    }
  }

  async function handleRender() {
    if (!project || !voiceId) return;
    setRendering(true);
    setRenderError(null);
    setRenderProgress(null);
    try {
      // 1. Enqueue the job
      const enqueue = await projects.renderAsync(project.id, ttsProvider, voiceId);
      // 2. Subscribe to progress events (Server-Sent Events)
      const ev = new EventSource(enqueue.events_url, { withCredentials: true } as EventSourceInit);
      eventSourceRef.current = ev;

      ev.addEventListener('snapshot', (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data);
          if (data.progress) setRenderProgress(data.progress);
        } catch {}
      });
      ev.addEventListener('progress', (e) => {
        try {
          const p = JSON.parse((e as MessageEvent).data);
          setRenderProgress(p);
        } catch {}
      });
      ev.addEventListener('completed', (e) => {
        try {
          const result = JSON.parse((e as MessageEvent).data);
          if (result?.renderId) {
            setRenderId(result.renderId);
            setAudioStale(false);
            setProject((cur) =>
              cur
                ? {
                    ...cur,
                    last_render_id: result.renderId,
                    last_render_provider: result.provider,
                    last_render_voice_id: result.voiceId,
                    last_render_size_bytes: result.sizeBytes,
                    last_rendered_at: new Date().toISOString(),
                  }
                : cur,
            );
          }
        } catch {}
        ev.close();
        eventSourceRef.current = null;
        setRendering(false);
        setRenderProgress(null);
        toast.success('Render complete', 'Your audio is ready to play.');
      });
      ev.addEventListener('failed', (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data);
          setRenderError(data?.error ?? 'Render failed.');
          toast.error('Render failed', data?.error ?? undefined);
        } catch {
          setRenderError('Render failed.');
          toast.error('Render failed');
        }
        ev.close();
        eventSourceRef.current = null;
        setRendering(false);
        setRenderProgress(null);
      });
      ev.onerror = () => {
        // EventSource auto-reconnects; only treat as fatal if we're already done.
        // Fastify writes events directly to reply.raw and closes on completion,
        // which triggers an `error` event in the browser — ignore if we have a
        // result already.
      };
    } catch (err) {
      setRenderError(err instanceof ApiError ? err.message : 'Render failed.');
      setRendering(false);
    }
  }

  // Cue edits — local edit, persist via PUT /ir, mark audio as stale.
  async function handleChangeIr(nextIr: import('@scriptvoice/ir').Ir) {
    if (!project) return;
    setProject({ ...project, ir: nextIr });
    setAudioStale(true);
    try {
      await projects.putIr(project.id, nextIr);
    } catch (err) {
      // Roll back optimistic update
      setProject({ ...project, ir: project.ir });
      setRegenError(err instanceof ApiError ? err.message : 'Failed to save edit.');
    }
  }

  // Section re-direct — calls the LLM to regenerate one section with a tone
  // steering preset; splices the result back into the project's IR.
  async function handleRegenSection(sectionId: string, tone: string) {
    if (!project) return;
    setBusySectionId(sectionId);
    setRegenError(null);
    try {
      const r = await projects.redirectSection(project.id, sectionId, tone);
      setProject({
        ...project,
        ir: r.ir,
        director_provider: r.provider,
        director_model: r.model,
      });
      setAudioStale(true);
    } catch (err) {
      setRegenError(err instanceof ApiError ? err.message : 'Re-direct failed.');
    } finally {
      setBusySectionId(null);
    }
  }

  async function handleSaveScript() {
    if (!project) return;
    try {
      const r = await projects.update(project.id, { script: scriptDraft });
      setProject(r.project);
      setEditingScript(false);
      setRenderId(null); // IR was invalidated
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save script.');
    }
  }

  async function handleSaveTitle() {
    if (!project || titleDraft.trim() === project.title) {
      setEditingTitle(false);
      return;
    }
    try {
      const r = await projects.update(project.id, { title: titleDraft.trim() || 'Untitled' });
      setProject(r.project);
      setEditingTitle(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save title.');
    }
  }

  async function handleDelete() {
    if (!project) return;
    if (!confirm(`Delete "${project.title}" permanently? Audio renders for this project will also be lost.`)) return;
    try {
      await projects.remove(project.id);
      navigate('/projects');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete.');
    }
  }

  if (error) {
    return (
      <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2">
        {error}
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={20} label="Loading project…" />
      </div>
    );
  }

  return (
    <div>
      {/* Title row */}
      <div className="flex items-center gap-3 mb-2">
        {editingTitle ? (
          <input
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={handleSaveTitle}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleSaveTitle(); if (e.key === 'Escape') { setTitleDraft(project.title); setEditingTitle(false); } }}
            autoFocus
            className="font-serif text-3xl bg-transparent border-b border-soft focus:border-accent outline-none flex-1 py-1"
          />
        ) : (
          <h1
            onClick={() => setEditingTitle(true)}
            className="font-serif text-3xl cursor-text hover:text-ink/80"
          >
            {project.title}
          </h1>
        )}
        <span className="text-xs text-muted/70">
          {scriptDraft.split(/\s+/).filter(Boolean).length} words
        </span>
      </div>
      <p className="text-muted text-sm mb-8">
        {project.intent && <span>For: {project.intent}. </span>}
        {project.tone_preference && <span>Tone: {project.tone_preference}. </span>}
        {project.director_model && (
          <span className="text-muted/70">
            Directed via <span className="font-mono text-xs">{project.director_model}</span>
            {project.prompt_version && ` (prompt ${project.prompt_version})`}
          </span>
        )}
      </p>

      {/* Three-pane layout — stacks on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)] gap-6">
        {/* Left: raw script */}
        <aside>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-wider text-muted">Script</h2>
            {editingScript ? (
              <div className="flex gap-1">
                <button onClick={handleSaveScript} className="text-xs text-accent hover:underline">save</button>
                <span className="text-xs text-muted/50">·</span>
                <button onClick={() => { setScriptDraft(project.script_text); setEditingScript(false); }} className="text-xs text-muted hover:text-ink">cancel</button>
              </div>
            ) : (
              <button onClick={() => setEditingScript(true)} className="text-xs text-muted hover:text-ink">edit</button>
            )}
          </div>
          {editingScript ? (
            <textarea
              value={scriptDraft}
              onChange={(e) => setScriptDraft(e.target.value)}
              rows={20}
              className="w-full px-3 py-3 bg-white border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 font-serif text-base leading-relaxed resize-y"
            />
          ) : (
            <div className="bg-white border border-soft rounded-md px-4 py-4 font-serif text-base leading-relaxed whitespace-pre-wrap max-h-[70vh] overflow-y-auto">
              {project.script_text}
            </div>
          )}
          {editingScript && (
            <p className="text-xs text-muted/70 mt-2">
              Saving will clear the directed IR and any rendered audio — you’ll need to re-direct.
            </p>
          )}
        </aside>

        {/* Centre: directed view */}
        <section>
          <h2 className="text-xs uppercase tracking-wider text-muted mb-3">Directed view</h2>
          {!project.ir ? (
            <div className="bg-white border border-soft rounded-md px-6 py-8">
              <p className="text-muted text-sm leading-relaxed mb-4">
                The director hasn’t read this script yet.
              </p>
              <DirectorChooser value={directorChoice} onChange={setDirectorChoice} />
              {directError && (
                <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2 mt-4">
                  {directError}
                </div>
              )}
              <button
                onClick={handleDirect}
                disabled={directing}
                className="mt-4 bg-ink text-parchment px-5 py-2 rounded-md hover:bg-ink/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm tracking-wide"
              >
                {directing ? `Directing via ${directorChoice}…` : `Direct via ${directorChoice}`}
              </button>
              {directProgress && directProgress.chunksTotal > 1 && (
                <div className="mt-3 border border-soft rounded-md px-3 py-2 bg-soft/30">
                  <div className="flex justify-between text-xs text-muted mb-1.5">
                    <span>{directProgress.message}</span>
                    <span className="font-mono">{directProgress.chunksDone}/{directProgress.chunksTotal} chunks</span>
                  </div>
                  <div className="h-1.5 bg-soft rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent transition-all duration-300"
                      style={{
                        width:
                          directProgress.chunksTotal > 0
                            ? `${Math.min(100, (directProgress.chunksDone / directProgress.chunksTotal) * 100)}%`
                            : '0%',
                      }}
                    />
                  </div>
                  {directProgress.etaMs !== null && directProgress.etaMs > 0 && (
                    <div className="text-[11px] text-muted/70 mt-1.5 text-right">
                      about {Math.round(directProgress.etaMs / 1000)}s remaining
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-muted/70 mt-3">
                Short scripts: 8–60s. Long scripts (3000+ words) are auto-chunked and run in the background — leave this tab open.
              </p>
            </div>
          ) : (
            <div className="bg-white border border-soft rounded-md px-6 py-6 max-h-[80vh] overflow-y-auto">
              <DirectedView
                ir={project.ir}
                ttsProvider={ttsProvider}
                onChangeIr={handleChangeIr}
                onRegenSection={handleRegenSection}
                busySectionId={busySectionId}
              />
            </div>
          )}
          {regenError && (
            <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2 mt-3">
              {regenError}
            </div>
          )}
          {project.ir && (
            <div className="mt-3 flex items-center gap-3 text-xs text-muted">
              <DirectorChooser value={directorChoice} onChange={setDirectorChoice} compact />
              <button
                onClick={handleDirect}
                disabled={directing}
                className="text-muted hover:text-ink"
              >
                {directing ? 'Re-directing…' : 'Re-direct'}
              </button>
              <span className="text-muted/40">·</span>
              <a
                href={projects.exportMd(project.id)}
                className="text-muted hover:text-ink"
              >
                Export .md
              </a>
              <span className="text-muted/40">·</span>
              <a
                href={projects.exportJson(project.id)}
                className="text-muted hover:text-ink"
              >
                Export .json
              </a>
              <span className="text-muted/40">·</span>
              <button
                onClick={() => setHistoryOpen(true)}
                className="text-muted hover:text-ink"
              >
                History
              </button>
            </div>
          )}
        </section>

        {/* Right: render controls */}
        <aside>
          <h2 className="text-xs uppercase tracking-wider text-muted mb-3">Voice & render</h2>
          <div className="bg-white border border-soft rounded-md px-4 py-4 space-y-4">
            <div>
              <label className="block text-xs text-muted mb-1.5">TTS provider</label>
              <div className="grid grid-cols-3 gap-1">
                {TTS_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => { setTtsProvider(opt.id); setVoiceId(null); }}
                    className={`text-xs px-2 py-1.5 rounded transition-colors ${
                      ttsProvider === opt.id
                        ? 'bg-ink text-parchment'
                        : 'bg-soft/60 text-muted hover:bg-soft hover:text-ink'
                    }`}
                    title={opt.hint}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs text-muted mb-1.5">Voice</label>
              <VoicePicker
                provider={ttsProvider}
                selectedVoiceId={voiceId}
                onSelect={setVoiceId}
              />
              <p className="text-[10px] text-muted/60 mt-1">
                Tip: paste <span className="font-mono">clone:&lt;uuid&gt;</span> to use a
                voice clone (from the Voices page).
              </p>
            </div>

            {project.ir && (
              <div className="border-t border-soft pt-4">
                <button
                  type="button"
                  onClick={() => setShowMultiVoice((v) => !v)}
                  className="flex items-center justify-between w-full text-xs text-muted hover:text-ink"
                >
                  <span>Per-role voices (multi-voice)</span>
                  <span className="text-muted/60">{showMultiVoice ? '−' : '+'}</span>
                </button>
                {showMultiVoice && (
                  <div className="mt-3">
                    <MultiVoicePanel projectId={project.id} ir={project.ir} />
                  </div>
                )}
              </div>
            )}

            {project.ir ? (
              <button
                onClick={handleRender}
                disabled={!voiceId || rendering}
                className="w-full bg-accent text-parchment py-2 rounded-md hover:bg-accent/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm tracking-wide"
              >
                {rendering ? 'Rendering audio…' : 'Render'}
              </button>
            ) : (
              <p className="text-xs text-muted/70 text-center py-2">
                Direct the script first, then render.
              </p>
            )}

            {renderProgress && (
              <div className="border border-soft rounded-md px-3 py-2 bg-soft/30">
                <div className="flex justify-between text-xs text-muted mb-1.5">
                  <span>{renderProgress.message}</span>
                  <span className="font-mono">
                    {renderProgress.sectionsDone}/{renderProgress.sectionsTotal}
                  </span>
                </div>
                <div className="h-1.5 bg-soft rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent transition-all duration-300"
                    style={{
                      width:
                        renderProgress.sectionsTotal > 0
                          ? `${Math.min(100, (renderProgress.sectionsDone / renderProgress.sectionsTotal) * 100)}%`
                          : '0%',
                    }}
                  />
                </div>
                {renderProgress.etaMs !== null && renderProgress.etaMs > 0 && (
                  <div className="text-[11px] text-muted/70 mt-1.5 text-right">
                    about {Math.round(renderProgress.etaMs / 1000)}s remaining
                  </div>
                )}
              </div>
            )}

            {renderError && (
              <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2">
                {renderError}
              </div>
            )}

            {project.ir && (
              <AudioSettingsPanel
                project={project}
                onUpdated={(next) => setProject(next)}
              />
            )}

                        {renderId && (
              <div className="border-t border-soft pt-4">
                <div className="text-xs text-muted mb-2 flex items-center gap-2">
                  <span>Latest render</span>
                  {audioStale && (
                    <span className="text-[10px] uppercase tracking-wider bg-accent/10 text-accent border border-accent/20 rounded px-1.5 py-0.5">
                      out of date
                    </span>
                  )}
                </div>
                <AudioPlayer
                  src={projects.audioUrl(renderId)}
                  downloadName={`${project.title.replace(/[^\w.-]+/g, '_')}.mp3`}
                />
              </div>
            )}
          </div>

          <button
            onClick={handleDelete}
            className="block mx-auto mt-6 text-xs text-muted/60 hover:text-accent transition-colors"
          >
            Delete project
          </button>
        </aside>
      </div>

      {historyOpen && (
        <HistoryDrawer
          projectId={project.id}
          onClose={() => setHistoryOpen(false)}
          onReverted={(ir) => {
            setProject((p) => (p ? { ...p, ir } : p));
            setHistoryOpen(false);
          }}
        />
      )}
    </div>
  );
}

function DirectorChooser({
  value,
  onChange,
  compact,
}: {
  value: DirectorChoice;
  onChange: (v: DirectorChoice) => void;
  compact?: boolean;
}) {
  return (
    <div className={`flex gap-1 flex-wrap ${compact ? '' : 'mt-3'}`}>
      {DIRECTOR_OPTIONS.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={`text-xs px-2.5 py-1 rounded transition-colors ${
            value === opt.id
              ? 'bg-ink text-parchment'
              : 'bg-soft/60 text-muted hover:bg-soft hover:text-ink'
          }`}
          title={opt.hint}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
